<template>
  <v-dialog v-model="visible" persistent max-width="700" scrollable @click:outside="close">
    <v-card>
      <!-- Header -->
      <v-toolbar color="success darken-2" dark flat>
        <v-toolbar-title class="font-weight-bold text-subtitle-1">
          Green Beans Quality Grading
        </v-toolbar-title>
        <v-spacer></v-spacer>
        <v-btn icon dark @click="close">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-toolbar>

      <v-card-text class="pa-0" style="max-height: calc(90vh - 120px); overflow-y: auto;">
        <!-- Loading state -->
        <div v-if="loading" class="d-flex justify-center align-center pa-10">
          <v-progress-circular indeterminate color="success"></v-progress-circular>
        </div>

        <template v-else-if="detail">
          <!-- Summary Row -->
          <div class="px-6 pt-5 pb-4">
            <v-row>
              <v-col cols="12" sm="4">
                <div class="field-label">Outturn Number</div>
                <div class="field-value">{{ detail.outturnNumber }}</div>
              </v-col>
              <v-col cols="12" sm="4">
                <div class="field-label">Total Quantity</div>
                <div class="field-value">{{ detail.totalGreenBeans != null ? detail.totalGreenBeans + ' kg' : '—' }}</div>
              </v-col>
              <v-col cols="12" sm="4">
                <div class="field-label">Status</div>
                <v-btn x-small rounded depressed class="success lighten-3 success--text mt-1" height="26">
                  {{ detail.status || 'Complete' }}
                </v-btn>
              </v-col>
              <v-col cols="12" sm="4">
                <div class="field-label">Start Date</div>
                <div class="field-value">{{ formatDate(detail.purchaseDate) }}</div>
              </v-col>
            </v-row>
          </div>

          <v-divider></v-divider>

          <!-- Parchment Information -->
          <div class="section-header d-flex justify-space-between align-center px-6 py-3" @click="toggleSection('parchment')">
            <span class="section-title">Parchment Information</span>
            <v-icon>{{ sections.parchment ? 'mdi-chevron-up' : 'mdi-chevron-down' }}</v-icon>
          </div>
          <v-expand-transition>
            <div v-if="sections.parchment">
              <template v-if="detail.batches && detail.batches.length">
                <div v-for="(batch, idx) in detail.batches" :key="idx">
                  <div class="px-6 py-4">
                    <v-row>
                      <v-col cols="12" sm="4">
                        <div class="field-label">Batch ID</div>
                        <div class="field-value">{{ batch.batchId }}</div>
                      </v-col>
                      <v-col cols="12" sm="4">
                        <div class="field-label">Quantity</div>
                        <div class="field-value">{{ batch.quantity != null ? batch.quantity + ' kg' : '—' }}</div>
                      </v-col>
                      <v-col cols="12" sm="4">
                      <div class="field-label">Supplier</div>
                      <div class="field-value">{{ batch.supplier }}</div>
                    </v-col>
                    <v-col cols="12" sm="4">
                      <div class="field-label">Species</div>
                      <div class="field-value">{{ batch.species }}</div>
                    </v-col>
                    <v-col cols="12" sm="4">
                      <div class="field-label">Variety</div>
                      <div class="field-value">{{ batch.variety }}</div>
                    </v-col>
                    <v-col cols="12" sm="4">
                      <div class="field-label">Process</div>
                      <div class="field-value">{{ batch.process }}</div>
                    </v-col>
                    <v-col cols="12" sm="4">
                      <div class="field-label">Batch Rating</div>
                      <div class="field-value">{{ batch.batchRating || '—' }}</div>
                    </v-col>
                    <v-col cols="12" sm="4">
                      <div class="field-label">Humidity</div>
                      <div class="field-value">{{ batch.humidity != null ? batch.humidity + ' %' : '—' }}</div>
                    </v-col>
                    <v-col cols="12" sm="4">
                      <div class="field-label">Density</div>
                      <div class="field-value">{{ batch.density != null ? batch.density + ' kg/cm3' : '—' }}</div>
                    </v-col>
                    <v-col cols="12" sm="4">
                      <div class="field-label">Total Coffee Cherry Qty</div>
                      <div class="field-value">{{ batch.totalCoffeeCherryQty != null ? batch.totalCoffeeCherryQty + ' kg' : '—' }}</div>
                    </v-col>
                    </v-row>
                  </div>
                  <v-divider v-if="idx < detail.batches.length - 1"></v-divider>
                </div>
              </template>
              <div v-else class="px-6 py-4 text--secondary text-body-2">No parchment batches linked.</div>
            </div>
          </v-expand-transition>

          <v-divider></v-divider>

          <!-- Process Information -->
          <div class="section-header d-flex justify-space-between align-center px-6 py-3" @click="toggleSection('process')">
            <span class="section-title">Process Information</span>
            <v-icon>{{ sections.process ? 'mdi-chevron-up' : 'mdi-chevron-down' }}</v-icon>
          </div>
          <v-expand-transition>
            <div v-if="sections.process" class="px-6 py-4">
              <v-row>
                <v-col cols="12" sm="4">
                  <div class="field-label">Air Humidity</div>
                  <div class="field-value">{{ detail.airHumidity != null ? detail.airHumidity : '—' }}</div>
                </v-col>
                <v-col cols="12" sm="4">
                  <div class="field-label">Density</div>
                  <div class="field-value">{{ detail.density != null ? detail.density + (detail.densityUnit ? ' ' + detail.densityUnit : ' kg/cm3') : '—' }}</div>
                </v-col>
                <v-col cols="12" sm="4">
                  <div class="field-label">Batch Production Density</div>
                  <div class="field-value">{{ detail.batchProductionDensity != null ? detail.batchProductionDensity + ' kg/cm3' : '—' }}</div>
                </v-col>
                <v-col cols="12" sm="4">
                  <div class="field-label">Quality Score</div>
                  <div class="field-value">{{ detail.qualityScore }}</div>
                </v-col>
                <v-col cols="12" sm="4">
                  <div class="field-label">Weight before Processing</div>
                  <div class="field-value">{{ detail.weightBeforeProcessing != null ? detail.weightBeforeProcessing + ' kg' : '—' }}</div>
                </v-col>
                <v-col cols="12" sm="4">
                  <div class="field-label">Percentage Primary Defect</div>
                  <div class="field-value">{{ detail.primaryDefect != null ? detail.primaryDefect + '%' : '—' }}</div>
                </v-col>
                <v-col cols="12" sm="4">
                  <div class="field-label">Percentage Secondary Defect</div>
                  <div class="field-value">{{ detail.secondaryDefect != null ? detail.secondaryDefect + '%' : '—' }}</div>
                </v-col>
              </v-row>
            </div>
          </v-expand-transition>

          <v-divider></v-divider>

          <!-- Green Beans Information -->
          <div class="section-header d-flex justify-space-between align-center px-6 py-3" @click="toggleSection('greenBeans')">
            <span class="section-title">Green Beans Information</span>
            <v-icon>{{ sections.greenBeans ? 'mdi-chevron-up' : 'mdi-chevron-down' }}</v-icon>
          </div>
          <v-expand-transition>
            <div v-if="sections.greenBeans" class="px-6 py-4">
              <v-row>
                <v-col cols="12" sm="4">
                  <div class="field-label">Outturn Number</div>
                  <div class="field-value">{{ detail.greenBeansId }}</div>
                </v-col>
                <v-col cols="12" sm="4">
                  <div class="field-label">Total Green Beans</div>
                  <div class="field-value">{{ detail.totalGreenBeans != null ? detail.totalGreenBeans + ' kg' : '—' }}</div>
                </v-col>
                <v-col cols="12" sm="4">
                  <div class="field-label">Number of Bags</div>
                  <div class="field-value">{{ detail.numberOfBags != null ? detail.numberOfBags : '—' }}</div>
                </v-col>
                <v-col cols="12" sm="4">
                  <div class="field-label">Leftover Quantity</div>
                  <div class="field-value">{{ detail.leftoverQuantity != null ? detail.leftoverQuantity + ' kg' : '—' }}</div>
                </v-col>
                <v-col cols="12" sm="4">
                  <div class="field-label">Quality Score</div>
                  <div class="field-value">{{ detail.greenBeansLabel }}</div>
                </v-col>
                <v-col cols="12" sm="4">
                  <div class="field-label">Expiry Date</div>
                  <div class="field-value">{{ formatDate(detail.expiryDate) }}</div>
                </v-col>
              </v-row>
              <v-row class="mt-4">
                <v-col cols="12" sm="4">
                  <div class="field-label">QR Code</div>
                  <div class="mt-2">
                    <v-img
                      v-if="detail.qrCodeUrl"
                      :src="detail.qrCodeUrl"
                      max-width="120"
                      max-height="120"
                      contain
                    ></v-img>
                  </div>
                </v-col>
              </v-row>
            </div>
          </v-expand-transition>

          <v-divider></v-divider>

          <!-- Quality Control -->
          <div class="section-header d-flex justify-space-between align-center px-6 py-3" @click="toggleSection('quality')">
            <span class="section-title">Quality Control</span>
            <v-icon>{{ sections.quality ? 'mdi-chevron-up' : 'mdi-chevron-down' }}</v-icon>
          </div>
          <v-expand-transition>
            <div v-if="sections.quality">
              <!-- Pie chart -->
              <div v-if="pieSegments.length" class="d-flex align-center px-6 pt-4 pb-2">
                <svg width="160" height="160" viewBox="0 0 160 160" class="flex-shrink-0">
                  <path v-for="seg in pieSegments" :key="'pie-' + seg.lotId" :d="seg.path" :fill="seg.color" />
                </svg>
                <div class="ml-6">
                  <div v-for="seg in chartSegments" :key="'legend-' + seg.lotId" class="d-flex align-center mb-2">
                    <span class="legend-dot mr-2" :style="{ backgroundColor: seg.color }"></span>
                    <span class="text-body-2">{{ seg.lotId }}: {{ seg.percentage }}%</span>
                  </div>
                </div>
              </div>

              <!-- Lot cards -->
              <div v-for="(lot, idx) in detail.qualityGradings" :key="'lot-' + idx">
                <v-divider></v-divider>
                <div class="px-6 py-4">
                  <div class="lot-id mb-3">Lot ID: {{ lot.lotId || '—' }}</div>
                  <v-row>
                    <v-col cols="12" sm="4">
                      <div class="field-label">Quantity</div>
                      <div class="field-value">{{ lot.quantity != null ? lot.quantity + ' kg' : '—' }}</div>
                    </v-col>
                    <v-col cols="12" sm="4">
                      <div class="field-label">Packing</div>
                      <div class="field-value">{{ lot.packing }}</div>
                    </v-col>
                    <v-col cols="12" sm="4">
                      <div class="field-label">Total Bags</div>
                      <div class="field-value">{{ lot.pocketQuantity != null ? lot.pocketQuantity : '—' }}</div>
                    </v-col>
                    <v-col cols="12" sm="4">
                      <div class="field-label">Leftover Quantity</div>
                      <div class="field-value">{{ computeLeftover(lot) }}</div>
                    </v-col>
                    <v-col cols="12" sm="4">
                      <div class="field-label">Quality Score</div>
                      <div class="field-value">{{ lot.qualityScore || '—' }}</div>
                    </v-col>
                    <v-col cols="12" sm="4">
                      <div class="field-label">Label</div>
                      <div class="field-value">{{ lot.label || '—' }}</div>
                    </v-col>
                    <v-col cols="12" sm="4">
                      <div class="field-label">Percentage</div>
                      <div class="field-value">{{ lot.percentage != null ? lot.percentage + '%' : '—' }}</div>
                    </v-col>
                    <v-col cols="12" sm="4">
                      <div class="field-label">Latest Cupping Score</div>
                      <div class="field-value">
                        {{ detail.latestCuppingScore != null ? detail.latestCuppingScore : '—' }}
                        <span v-if="detail.latestCuppingScore != null" class="ml-1">
                          <a href="javascript:void(0)" class="link-text">Cupping History</a>
                        </span>
                      </div>
                    </v-col>
                  </v-row>
                  <v-row class="mt-4">
                    <v-col cols="12" sm="4">
                      <div class="field-label">Label</div>
                      <div class="mt-2">
                        <v-img
                          v-if="lot.labelUrl || detail.labelUrl"
                          :src="lot.labelUrl || detail.labelUrl"
                          max-width="120"
                          max-height="120"
                          contain
                        ></v-img>
                      </div>
                    </v-col>
                    <v-col cols="12" sm="8" class="d-flex align-center justify-center">
                      <v-btn
                        v-if="lot.labelUrl || detail.labelUrl"
                        color="brown darken-2"
                        class="white--text text-none px-6"
                        :href="lot.labelUrl || detail.labelUrl"
                        target="_blank"
                      >
                        DOWNLOAD LABEL
                      </v-btn>
                    </v-col>
                  </v-row>
                </div>
              </div>
              <div v-if="!detail.qualityGradings || !detail.qualityGradings.length" class="px-6 py-4 text--secondary text-body-2">
                No quality grades recorded.
              </div>
            </div>
          </v-expand-transition>

          <v-divider></v-divider>

          <!-- Waste -->
          <div class="section-header d-flex justify-space-between align-center px-6 py-3" @click="toggleSection('waste')">
            <span class="section-title">Waste</span>
            <v-icon>{{ sections.waste ? 'mdi-chevron-up' : 'mdi-chevron-down' }}</v-icon>
          </div>
          <v-expand-transition>
            <div v-if="sections.waste" class="px-6 py-4">
              <v-row>
                <v-col cols="12" sm="4">
                  <div class="field-label">Total Waste</div>
                  <div class="field-value">{{ detail.totalWaste != null ? detail.totalWaste + ' kg' : '—' }}</div>
                </v-col>
                <v-col cols="12" sm="4">
                  <div class="field-label">Percentage</div>
                  <div class="field-value">{{ wastePercentage }}</div>
                </v-col>
              </v-row>
            </div>
          </v-expand-transition>
        </template>

        <div v-else-if="!loading" class="pa-10 text-center text--secondary">No data found.</div>
      </v-card-text>

      <v-divider></v-divider>
      <v-card-actions class="pa-4">
        <v-spacer></v-spacer>
        <v-btn color="brown darken-2" class="white--text text-uppercase" depressed @click="close">Close</v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import moment from "moment";
import GreenCoffeeService from "@/_services/GreenCoffeeService";

const PIE_COLORS = ["#4caf50", "#ff9800", "#607d8b", "#2196f3", "#9c27b0", "#f44336", "#00bcd4"];

export default {
  name: "ViewGreenCoffeeModal",
  props: {
    value: { type: Boolean, default: false },
    itemId: { type: [Number, String], default: null },
  },
  data() {
    return {
      visible: this.value,
      loading: false,
      detail: null,
      sections: {
        parchment: true,
        process: true,
        greenBeans: true,
        quality: true,
        waste: true,
      },
    };
  },
  computed: {
    chartSegments() {
      if (!this.detail?.qualityGradings?.length) return [];
      return this.detail.qualityGradings.map((lot, i) => ({
        lotId: lot.lotId || `Lot ${i + 1}`,
        percentage: lot.percentage || 0,
        color: PIE_COLORS[i % PIE_COLORS.length],
      }));
    },
    pieSegments() {
      const segments = this.chartSegments.filter((s) => s.percentage > 0);
      const total = segments.reduce((s, l) => s + l.percentage, 0);
      if (total < 100) segments.push({ lotId: "__rest__", percentage: 100 - total, color: "#bdbdbd" });
      const cx = 80, cy = 80, r = 75;
      const paths = [];
      let currentAngle = -90;
      for (const seg of segments) {
        if (seg.percentage <= 0) continue;
        const sweep = (seg.percentage / 100) * 360;
        paths.push({ ...seg, path: this.describeSlice(cx, cy, r, currentAngle, currentAngle + sweep) });
        currentAngle += sweep;
      }
      return paths;
    },
    wastePercentage() {
      if (!this.detail) return "—";
      const waste = Number(this.detail.totalWaste) || 0;
      const total = Number(this.detail.totalGreenBeans) || 0;
      if (!total) return "—";
      return Math.round((waste / total) * 100) + " %";
    },
  },
  watch: {
    value(v) {
      this.visible = v;
      if (v && this.itemId) this.fetchDetail();
    },
    visible(v) {
      this.$emit("input", v);
    },
    itemId(v) {
      if (v && this.visible) this.fetchDetail();
    },
  },
  methods: {
    close() {
      this.visible = false;
      this.$emit("close");
    },
    toggleSection(key) {
      this.sections[key] = !this.sections[key];
    },
    formatDate(date) {
      return date ? moment(date).format("DD-MMM-YYYY").toUpperCase() : "—";
    },
    computeLeftover(lot) {
      const qty = Number(lot.quantity) || 0;
      const unit = Number(lot.unitSize) || 1;
      const bags = Math.floor(qty / unit);
      const leftover = qty - bags * unit;
      return leftover > 0 ? leftover + " kg" : "0 kg";
    },
    describeSlice(cx, cy, r, startDeg, endDeg) {
      const toRad = (deg) => (deg * Math.PI) / 180;
      const x1 = cx + r * Math.cos(toRad(startDeg));
      const y1 = cy + r * Math.sin(toRad(startDeg));
      const x2 = cx + r * Math.cos(toRad(endDeg));
      const y2 = cy + r * Math.sin(toRad(endDeg));
      const large = endDeg - startDeg > 180 ? 1 : 0;
      return `M ${cx} ${cy} L ${x1.toFixed(2)} ${y1.toFixed(2)} A ${r} ${r} 0 ${large} 1 ${x2.toFixed(2)} ${y2.toFixed(2)} Z`;
    },
    async fetchDetail() {
      this.loading = true;
      this.detail = null;
      try {
        const res = await GreenCoffeeService.getAdminGreenCoffeeById(this.itemId);
        if (res?.data) this.detail = res.data;
      } catch (e) {
        console.error("Failed to load green coffee detail:", e);
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>

<style scoped>
.section-header {
  cursor: pointer;
  user-select: none;
  background-color: #fafafa;
}
.section-header:hover {
  background-color: #f5f5f5;
}
.section-title {
  color: #6d4c41;
  font-weight: 700;
  font-size: 15px;
}
.lot-id {
  color: #6d4c41;
  font-weight: 700;
  font-size: 15px;
}
.field-label {
  font-size: 12px;
  color: rgba(0, 0, 0, 0.54);
  line-height: 1.4;
}
.field-value {
  font-weight: 700;
  font-size: 14px;
  color: rgba(0, 0, 0, 0.87);
  margin-top: 2px;
}
.legend-dot {
  width: 12px;
  height: 12px;
  border-radius: 50%;
  display: inline-block;
  flex-shrink: 0;
}
</style>
